import { useEffect, useRef, useState } from "react";
import { BAR_COUNT, COLORS, formatTime, useAudioRecorder } from "../hooks/useAudioRecorder";

const BG = COLORS.bg;
const YELLOW = COLORS.yellow;
const RED = COLORS.red;

export default function AudioTest() {
  const {
    status,
    elapsedMs,
    error,
    levels,
    silenceWarning,
    mimeType,
    blobSize,
    audioUrl,
    start,
    stop,
    reset,
  } = useAudioRecorder();

  const [isPlaying, setIsPlaying] = useState(false);
  const audioElRef = useRef(null);

  // Revoke audioUrl on unmount (hook doesn't know about <audio>).
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const isRecording = status === "recording";
  const hasRecording = status === "recorded" && !!audioUrl;

  const toggleRecord = () => {
    if (isRecording) stop();
    else start();
  };

  const togglePlay = () => {
    const a = audioElRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().catch(() => {});
    } else {
      a.pause();
    }
  };

  const buttonColor = isRecording ? RED : YELLOW;
  const buttonLabel = isRecording
    ? "Tap to Stop"
    : hasRecording
      ? "Tap to Re-record"
      : "Tap to Record";
  const subLabel = isRecording ? "RECORDING" : hasRecording ? "READY" : "MICROPHONE · IDLE";

  return (
    <div
      data-testid="audio-test-page"
      style={{
        minHeight: "100dvh",
        backgroundColor: BG,
        color: "#f4f1ea",
        fontFamily: "'Inter', system-ui, sans-serif",
        display: "flex",
        flexDirection: "column",
        padding: "24px 20px 40px",
        boxSizing: "border-box",
        WebkitTapHighlightColor: "transparent",
        userSelect: "none",
      }}
    >
      {/* Header */}
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            letterSpacing: "0.18em",
            color: "#6b6558",
            textTransform: "uppercase",
          }}
          data-testid="audio-test-title"
        >
          /audio-test
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            letterSpacing: "0.18em",
            color: isRecording ? RED : "#6b6558",
            textTransform: "uppercase",
          }}
          data-testid="audio-test-status-chip"
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              backgroundColor: isRecording ? RED : hasRecording ? YELLOW : "#4a4638",
              boxShadow: isRecording ? `0 0 12px ${RED}` : "none",
              transition: "background-color 180ms ease",
            }}
          />
          {subLabel}
        </div>
      </header>

      {/* Main */}
      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 40,
        }}
      >
        {/* Timer */}
        <div
          data-testid="audio-test-timer"
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 44,
            fontWeight: 500,
            letterSpacing: "0.04em",
            color: isRecording ? "#f4f1ea" : "#6b6558",
            fontVariantNumeric: "tabular-nums",
            transition: "color 180ms ease",
          }}
        >
          {formatTime(elapsedMs)}
        </div>

        {/* Silence warning */}
        <div
          data-testid="audio-test-silence-warning"
          aria-live="polite"
          style={{
            marginTop: -28,
            minHeight: 18,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            letterSpacing: "0.1em",
            color: "#c9a14a",
            opacity: isRecording && silenceWarning ? 0.9 : 0,
            transition: "opacity 240ms ease",
            textAlign: "center",
          }}
        >
          {isRecording && silenceWarning ? "// no sound detected — check mic permission" : ""}
        </div>

        {/* Record button */}
        <button
          data-testid="audio-test-record-btn"
          aria-label={buttonLabel}
          onClick={toggleRecord}
          style={{
            width: 232,
            height: 232,
            borderRadius: "50%",
            border: "none",
            backgroundColor: buttonColor,
            color: "#0f0e0c",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "'Inter', sans-serif",
            fontWeight: 700,
            fontSize: 20,
            letterSpacing: "-0.01em",
            boxShadow: isRecording
              ? `0 0 0 12px rgba(229,75,60,0.18), 0 30px 70px -20px rgba(229,75,60,0.55)`
              : `0 30px 70px -20px rgba(232,220,45,0.55)`,
            transition:
              "background-color 200ms ease, box-shadow 240ms ease, transform 120ms ease",
            transform: isRecording ? "scale(0.97)" : "scale(1)",
            padding: 24,
            textAlign: "center",
            lineHeight: 1.15,
          }}
        >
          {buttonLabel}
        </button>

        {/* Live level meter */}
        <div
          data-testid="audio-test-meter"
          aria-hidden={!isRecording}
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            gap: 5,
            height: 56,
            width: "100%",
            maxWidth: 320,
            opacity: isRecording ? 1 : 0,
            transition: "opacity 240ms ease",
            pointerEvents: "none",
          }}
        >
          {levels.map((lv, i) => {
            const v = Math.min(1, Math.pow(lv, 0.7));
            const h = 4 + v * 52;
            return (
              <span
                key={i}
                data-testid={`audio-test-meter-bar-${i}`}
                style={{
                  display: "inline-block",
                  width: 6,
                  height: `${h}px`,
                  backgroundColor: YELLOW,
                  borderRadius: 3,
                  boxShadow: v > 0.15 ? `0 0 8px rgba(232,220,45,0.55)` : "none",
                  transition: "height 80ms linear, box-shadow 160ms ease",
                }}
              />
            );
          })}
          {Array.from({ length: Math.max(0, BAR_COUNT - levels.length) }).map((_, i) => (
            <span key={`pad-${i}`} style={{ width: 6, height: 4 }} />
          ))}
        </div>

        {/* Playback controls */}
        {hasRecording && (
          <div
            data-testid="audio-test-playback-panel"
            style={{
              width: "100%",
              maxWidth: 380,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 16,
            }}
          >
            <audio
              ref={audioElRef}
              src={audioUrl}
              preload="auto"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              style={{ display: "none" }}
              data-testid="audio-test-audio-el"
            />
            <div style={{ display: "flex", gap: 12, width: "100%" }}>
              <button
                data-testid="audio-test-play-btn"
                onClick={togglePlay}
                style={{
                  flex: 1,
                  padding: "16px 20px",
                  borderRadius: 999,
                  border: `1px solid ${YELLOW}`,
                  backgroundColor: "transparent",
                  color: YELLOW,
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  fontSize: 15,
                  letterSpacing: "0.02em",
                  cursor: "pointer",
                  transition: "background-color 160ms ease, color 160ms ease",
                }}
              >
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                data-testid="audio-test-reset-btn"
                onClick={reset}
                style={{
                  flex: 1,
                  padding: "16px 20px",
                  borderRadius: 999,
                  border: "1px solid #3a362c",
                  backgroundColor: "transparent",
                  color: "#a8a292",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  fontSize: 15,
                  letterSpacing: "0.02em",
                  cursor: "pointer",
                }}
              >
                Discard
              </button>
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 11,
                color: "#6b6558",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                textAlign: "center",
              }}
              data-testid="audio-test-meta"
            >
              {mimeType} · {(blobSize / 1024).toFixed(1)} KB · {formatTime(elapsedMs)}
            </div>
          </div>
        )}
      </main>

      {/* Error banner */}
      {error && (
        <div
          data-testid="audio-test-error"
          role="alert"
          style={{
            marginTop: 24,
            padding: "14px 16px",
            borderRadius: 12,
            border: `1px solid ${RED}`,
            backgroundColor: "rgba(229,75,60,0.08)",
            color: "#ffb3ab",
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            lineHeight: 1.5,
            wordBreak: "break-word",
          }}
        >
          {error}
        </div>
      )}

      {/* Footer hint */}
      <footer
        style={{
          marginTop: 24,
          textAlign: "center",
          fontFamily: "'Inter', sans-serif",
          fontSize: 12,
          color: "#57533f",
        }}
        data-testid="audio-test-footer"
      >
        Grant microphone permission when prompted. Works on iOS Safari & Android Chrome.
      </footer>
    </div>
  );
}
